import React, { useState, useEffect, useRef } from "react";

/**
 * ISAPS Olympiad World Congress 2025 Landing Page - Aligned with ClinicOS Webflow Design
 * International Society of Aesthetic Plastic Surgery - Singapore
 * Matches the visual design system, typography, colors, and layout patterns
 */

const ISAPSOlympiadLanding = ({
  videoUrl = "https://player.vimeo.com/video/YOUR_VIDEO_ID",
  onSignUpClick = () =>
    window.open("https://dashboard.clinicos.ai/signup", "_blank"),
  onLoginClick = () =>
    window.open("https://dashboard.clinicos.ai/signin", "_blank"),
  showHeader = true,
  className = "",
  clinicName = null,
  userId = null,
  referralSource = "direct",
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [stats, setStats] = useState({
    surgeons: 1200,
    countries: 80,
    procedures: 25,
    discount: 40,
  });

  const pageLoadTime = useRef(Date.now());
  const sessionId = useRef(
    `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  );

  // Webflow-aligned color system
  const colors = {
    primary: "#00B7F4", // Matches nav-link-pri-btn
    primaryDark: "#0099CC",
    secondary: "#024767", // Matches background-image-024767
    orange: "#F97316", // Matches text-orange
    background: "#FFFFFF",
    cardBg: "#F9F9F9", // Matches before-after-card styling
    lightBg: "#F3F3F3", // Matches background-f3f3f3
    gradientBg: "#F8F8F8", // Matches background-image-f8f8f8
    title: "#111111",
    text: "#444444",
    subtext: "#666666",
    grayText: "#999999", // Matches text-color-gray
    border: "#E5E5E5",
    white: "#FFFFFF",
  };

  // Initialize tracking
  useEffect(() => {
    // Countdown timer - set to conference date (example: June 2025)
    const updateCountdown = () => {
      const now = new Date();
      const conferenceDate = new Date("2025-06-15T00:00:00+08:00"); // Singapore time

      const distance = conferenceDate.getTime() - now.getTime();

      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const trackEvent = (eventName, properties = {}) => {
    console.log("Tracking:", eventName, properties);
    // Add your analytics here
  };

  const playVideo = () => {
    setIsVideoPlaying(true);
    trackEvent("ISAPS Olympiad Video Started");
  };

  const copyPromoCode = () => {
    navigator.clipboard.writeText("ISAPS2025").then(() => {
      trackEvent("ISAPS Olympiad Promo Code Copied");
      alert("Promo code ISAPS2025 copied to clipboard!");
    });
  };

  const handleCTAClick = (location, ctaType = "signup") => {
    trackEvent("ISAPS Olympiad CTA Clicked", { location, ctaType });
    if (ctaType === "signup") {
      onSignUpClick();
    } else {
      onLoginClick();
    }
  };

  // Webflow-aligned styles
  const styles = {
    body: {
      fontFamily: "system-ui, -apple-system, sans-serif",
      backgroundColor: colors.background,
      color: colors.text,
      lineHeight: 1.5,
      margin: 0,
      padding: 0,
    },
    // Header - matches navbar styling
    navbar: {
      backgroundColor: colors.background,
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      padding: "16px 0",
    },
    navbarContent: {
      maxWidth: "1440px",
      margin: "0 auto",
      padding: "0 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    navLink: {
      color: colors.text,
      textDecoration: "none",
      fontSize: "16px",
      fontWeight: "400",
      marginRight: "32px",
      cursor: "pointer",
      transition: "color 0.2s ease",
    },
    navLinkPriBtn: {
      backgroundColor: colors.primary,
      color: colors.white,
      padding: "12px 24px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s ease",
    },
    navLinkSecBtn: {
      backgroundColor: "transparent",
      color: colors.text,
      padding: "12px 24px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: `1px solid ${colors.border}`,
      cursor: "pointer",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s ease",
      marginRight: "16px",
    },
    headerLogin: {
      color: colors.text,
      textDecoration: "none",
      fontSize: "16px",
      fontWeight: "500",
      marginRight: "24px",
      cursor: "pointer",
    },
    // Layout containers - matches Webflow structure
    outterWrapper: {
      maxWidth: "1440px",
      margin: "0 auto",
      padding: "0 20px",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 24px",
    },
    // Section spacing - matches Webflow patterns
    section: {
      paddingTop: "80px",
      paddingBottom: "80px",
    },
    paddingVertical40: {
      paddingTop: "40px",
      paddingBottom: "40px",
    },
    paddingVertical100: {
      paddingTop: "100px",
      paddingBottom: "100px",
    },
    // Hero section with gradient - matches background-gradient
    backgroundGradient: {
      background:
        "linear-gradient(135deg, rgba(0,183,244,0.05) 0%, rgba(255,255,255,1) 100%)",
      marginTop: showHeader ? "75px" : "0",
    },
    // Typography - matches Webflow hierarchy
    h1: {
      fontSize: "48px",
      fontWeight: "700",
      color: colors.title,
      lineHeight: "1.2",
      marginBottom: "24px",
      textAlign: "center",
    },
    h5: {
      fontSize: "32px",
      fontWeight: "600",
      color: colors.title,
      lineHeight: "1.3",
      marginBottom: "16px",
    },
    h6: {
      fontSize: "24px",
      fontWeight: "600",
      color: colors.title,
      lineHeight: "1.4",
      marginBottom: "16px",
    },
    fontSizeNormal: {
      fontSize: "18px",
      fontWeight: "400",
      color: colors.text,
      lineHeight: "1.6",
    },
    fontSizeSmall: {
      fontSize: "16px",
      fontWeight: "400",
      color: colors.text,
      lineHeight: "1.5",
    },
    textColorGray: {
      color: colors.grayText,
    },
    textOrange: {
      color: colors.orange,
      fontWeight: "600",
    },
    textAlignCenter: {
      textAlign: "center",
    },
    fontWeightBold: {
      fontWeight: "700",
    },
    // Cards - matches before-after-card-container styling
    beforeAfterCardContainer: {
      backgroundColor: colors.cardBg,
      borderRadius: "16px",
      padding: "32px 24px",
      textAlign: "center",
      border: `1px solid ${colors.border}`,
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      cursor: "pointer",
    },
    shadowBox: {
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    },
    borderRadius10: {
      borderRadius: "10px",
    },
    // Buttons - matches pri-btn and sec-btn
    priBtn: {
      backgroundColor: colors.primary,
      color: colors.white,
      padding: "16px 32px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s ease",
      textDecoration: "none",
    },
    secBtn: {
      backgroundColor: "transparent",
      color: colors.text,
      padding: "16px 32px",
      borderRadius: "24px",
      fontSize: "16px",
      fontWeight: "600",
      border: `1px solid ${colors.border}`,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s ease",
      textDecoration: "none",
    },
    // Layout utilities - matches Webflow flex classes
    flexH: {
      display: "flex",
      alignItems: "center",
    },
    flexV: {
      display: "flex",
      flexDirection: "column",
    },
    flexWrapCenter: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
    },
    justifyContentCenter: {
      justifyContent: "center",
    },
    centerDiv: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },
    gap20: {
      gap: "20px",
    },
    gap30: {
      gap: "30px",
    },
    // Background sections
    backgroundF3F3F3: {
      backgroundColor: colors.lightBg,
    },
    backgroundImageF8F8F8: {
      backgroundColor: colors.gradientBg,
    },
    backgroundImage024767: {
      backgroundColor: colors.secondary,
      color: colors.white,
    },
    // Responsive utilities
    width100: {
      width: "100%",
    },
    minWidth350: {
      minWidth: "350px",
    },
  };

  const ArrowRightIcon = () => (
    <svg
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 5l7 7-7 7"
      ></path>
    </svg>
  );

  const PlayIcon = () => (
    <svg
      fill="currentColor"
      viewBox="0 0 24 24"
      style={{ width: "24px", height: "24px" }}
    >
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  return (
    <div style={styles.body} className={className}>
      {/* Header - matches Webflow navbar */}
      {showHeader && (
        <div style={styles.navbar}>
          <div style={styles.navbarContent}>
            <a
              href="/"
              style={{
                color: colors.primary,
                fontSize: "24px",
                fontWeight: "700",
                textDecoration: "none",
              }}
            >
              ClinicOS
            </a>
            <div style={styles.flexH}>
              <nav style={styles.flexH}>
                <a
                  style={styles.navLink}
                  onClick={() =>
                    document
                      .getElementById("features")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Product
                </a>
                <a
                  style={styles.navLink}
                  onClick={() =>
                    document
                      .getElementById("demo")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Demo
                </a>
                <a
                  style={styles.navLink}
                  onClick={() =>
                    document
                      .getElementById("register")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                >
                  Congress
                </a>
              </nav>
              <div style={styles.flexH}>
                <a
                  style={styles.headerLogin}
                  onClick={() => handleCTAClick("header", "login")}
                >
                  Login
                </a>
                <button
                  style={styles.navLinkPriBtn}
                  onClick={() => handleCTAClick("header", "signup")}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = colors.primaryDark)
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = colors.primary)
                  }
                >
                  Book a Demo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section - matches background-gradient styling */}
      <div style={styles.backgroundGradient}>
        <div style={styles.outterWrapper}>
          <div
            style={{
              ...styles.section,
              ...styles.centerDiv,
              ...styles.gap30,
              minHeight: "65vh",
            }}
          >
            <div
              style={{
                ...styles.flexV,
                ...styles.gap20,
                ...styles.width100,
                maxWidth: "800px",
                textAlign: "center",
              }}
            >
              {/* Badge */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "rgba(0,183,244,0.1)",
                  color: colors.primary,
                  padding: "8px 16px",
                  borderRadius: "24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "24px",
                }}
              >
                <span style={{ marginRight: "8px" }}>🌏</span>
                ISAPS Olympiad World Congress 2025 • Singapore
              </div>

              <h1 style={styles.h1}>AI-Powered Aesthetic Surgery Workflows</h1>

              <p
                style={{
                  ...styles.fontSizeNormal,
                  ...styles.textAlignCenter,
                  marginBottom: "32px",
                }}
              >
                Join the world's leading plastic surgeons in Singapore to
                experience revolutionary AI simulation technology at the
                International Society of Aesthetic Plastic Surgery's premier
                congress.
              </p>

              {/* CTA Buttons */}
              <div
                style={{
                  ...styles.flexH,
                  ...styles.gap20,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  style={styles.priBtn}
                  onClick={() => handleCTAClick("hero", "signup")}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = colors.primaryDark;
                    e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = colors.primary;
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  Get Congress Access - 40% Off
                  <ArrowRightIcon />
                </button>
                <button
                  style={styles.secBtn}
                  onClick={() => handleCTAClick("hero", "signup")}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = colors.primary;
                    e.target.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.color = colors.text;
                  }}
                >
                  Try Platform Now
                </button>
              </div>

              <p
                style={{
                  ...styles.fontSizeSmall,
                  ...styles.textColorGray,
                  marginTop: "16px",
                }}
              >
                ✅ Create your first simulation to unlock congress discount
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section - matches card styling */}
      <div style={styles.outterWrapper}>
        <div style={styles.paddingVertical40}>
          <div
            style={{
              ...styles.flexWrapCenter,
              ...styles.gap30,
              ...styles.justifyContentCenter,
            }}
          >
            {[
              {
                number: `${stats.surgeons}+`,
                title: "Global Surgeons Expected",
                desc: "Leading plastic surgeons from 80+ countries advancing aesthetic practice",
              },
              {
                number: `${stats.procedures}+`,
                title: "AI Simulation Types",
                desc: "Complete aesthetic surgery simulation capabilities for every procedure",
              },
              {
                number: `${stats.discount}%`,
                title: "Congress Discount",
                desc: "Exclusive platform access discount for ISAPS attendees",
              },
            ].map((stat, index) => (
              <div
                key={index}
                style={{
                  ...styles.beforeAfterCardContainer,
                  ...styles.shadowBox,
                  maxWidth: "300px",
                  flex: "1 1 300px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-4px)";
                  e.target.style.boxShadow = "0 8px 30px rgba(0,183,244,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)";
                }}
              >
                <h5
                  style={{
                    ...styles.h5,
                    color: colors.primary,
                    fontSize: "48px",
                    marginBottom: "16px",
                  }}
                >
                  {stat.number}
                </h5>
                <p
                  style={{
                    ...styles.fontWeightBold,
                    ...styles.fontSizeNormal,
                    marginBottom: "8px",
                  }}
                >
                  {stat.title}
                </p>
                <p
                  style={{
                    ...styles.textColorGray,
                    ...styles.textAlignCenter,
                    ...styles.fontSizeSmall,
                  }}
                >
                  {stat.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video Demo Section */}
      <section id="demo" style={styles.backgroundF3F3F3}>
        <div style={styles.outterWrapper}>
          <div
            style={{ ...styles.section, ...styles.centerDiv, ...styles.gap30 }}
          >
            <div style={styles.textAlignCenter}>
              <p
                style={{
                  ...styles.textOrange,
                  ...styles.fontSizeNormal,
                  marginBottom: "16px",
                }}
              >
                Congress Preview
              </p>
              <h6 style={styles.h6}>ClinicOS at ISAPS Olympiad 2025</h6>
              <p
                style={{
                  ...styles.fontSizeNormal,
                  ...styles.textColorGray,
                  marginBottom: "48px",
                  maxWidth: "600px",
                }}
              >
                Watch how international plastic surgeons are revolutionizing
                patient consultations with AI-powered before/after simulations
                in Singapore
              </p>
            </div>

            <div
              style={{
                backgroundColor: colors.background,
                borderRadius: "16px",
                overflow: "hidden",
                ...styles.shadowBox,
                maxWidth: "800px",
                width: "100%",
              }}
            >
              <div
                style={{
                  backgroundColor: "#000",
                  position: "relative",
                  paddingBottom: "56.25%",
                  width: "100%",
                }}
              >
                {!isVideoPlaying ? (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      background:
                        "linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), linear-gradient(135deg, rgba(0,183,244,0.1) 0%, rgba(255,255,255,0.9) 100%)",
                    }}
                    onClick={playVideo}
                  >
                    <button
                      style={{
                        width: "80px",
                        height: "80px",
                        backgroundColor: colors.primary,
                        borderRadius: "50%",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        boxShadow: "0 4px 20px rgba(0, 183, 244, 0.4)",
                      }}
                    >
                      <PlayIcon />
                    </button>
                  </div>
                ) : (
                  <iframe
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                    src={`${videoUrl}?autoplay=1`}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Countdown Section */}
      <div style={styles.backgroundImageF8F8F8}>
        <div style={styles.outterWrapper}>
          <div
            style={{ ...styles.section, ...styles.centerDiv, ...styles.gap30 }}
          >
            <h6 style={{ ...styles.h6, ...styles.textAlignCenter }}>
              Congress Early Access Ends Soon
            </h6>

            <div
              style={{
                backgroundColor: colors.background,
                borderRadius: "20px",
                padding: "40px",
                border: `1px solid ${colors.border}`,
                ...styles.shadowBox,
                textAlign: "center",
                maxWidth: "800px",
              }}
            >
              <p style={{ ...styles.fontSizeNormal, marginBottom: "30px" }}>
                Secure your 40% platform discount before ISAPS Olympiad begins
              </p>

              <div
                style={{
                  ...styles.flexH,
                  justifyContent: "center",
                  ...styles.gap20,
                  flexWrap: "wrap",
                  margin: "30px 0",
                }}
              >
                {Object.entries(countdown).map(([unit, value]) => (
                  <div
                    key={unit}
                    style={{
                      backgroundColor: colors.cardBg,
                      borderRadius: "12px",
                      padding: "20px 16px",
                      minWidth: "100px",
                      textAlign: "center",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "48px",
                        fontWeight: "700",
                        color: colors.primary,
                        lineHeight: "1",
                      }}
                    >
                      {value}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: colors.subtext,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        marginTop: "8px",
                        fontWeight: "500",
                      }}
                    >
                      {unit}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  backgroundColor: "#E1F7FE",
                  border: `2px solid ${colors.primary}`,
                  borderRadius: "20px",
                  padding: "24px",
                  margin: "30px 0",
                  cursor: "pointer",
                }}
                onClick={copyPromoCode}
              >
                <div
                  style={{
                    ...styles.fontSizeSmall,
                    ...styles.textColorGray,
                    marginBottom: "8px",
                  }}
                >
                  Use Promo Code:
                </div>
                <div
                  style={{
                    fontSize: "40px",
                    fontWeight: "700",
                    color: colors.primary,
                    fontFamily: "'Courier New', monospace",
                    letterSpacing: "2px",
                  }}
                >
                  ISAPS2025
                </div>
              </div>

              <button
                style={styles.priBtn}
                onClick={() => handleCTAClick("countdown", "signup")}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryDark;
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Sign Up & Create Simulation
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features">
        <div style={styles.outterWrapper}>
          <div style={styles.section}>
            <div style={{ ...styles.textAlignCenter, marginBottom: "48px" }}>
              <p
                style={{
                  ...styles.textOrange,
                  ...styles.fontSizeNormal,
                  marginBottom: "16px",
                }}
              >
                Congress Experience
              </p>
              <h6 style={styles.h6}>
                What You'll Experience at ISAPS Olympiad Singapore
              </h6>
              <p
                style={{
                  ...styles.fontSizeNormal,
                  ...styles.textColorGray,
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Discover cutting-edge AI simulation technology with the world's
                leading plastic surgeons in Singapore
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "24px",
              }}
            >
              {[
                {
                  icon: "🎯",
                  title: "Advanced Aesthetic Simulations",
                  subtitle: "AI-powered before/after visualizations",
                  description:
                    "Live demonstrations of next-generation algorithms creating photorealistic simulations for 25+ aesthetic procedures.",
                },
                {
                  icon: "🌏",
                  title: "Global Case Studies",
                  subtitle: "International surgeon success stories",
                  description:
                    "Learn from plastic surgeons worldwide who have revolutionized their practices with AI simulation technology.",
                },
                {
                  icon: "📊",
                  title: "Conversion Analytics",
                  subtitle: "Consultation ROI tracking and insights",
                  description:
                    "Master consultation metrics, patient engagement analytics, and conversion optimization strategies from global experts.",
                },
                {
                  icon: "🔒",
                  title: "International Compliance",
                  subtitle: "Multi-country regulatory standards",
                  description:
                    "Understand compliance frameworks across different countries including HIPAA, GDPR, and Singapore's healthcare regulations.",
                },
                {
                  icon: "⚡",
                  title: "Real-Time Processing",
                  subtitle: "Instant simulation generation",
                  description:
                    "Experience generating publication-quality simulations in seconds during live patient consultations on stage.",
                },
                {
                  icon: "🏆",
                  title: "Elite Networking",
                  subtitle: "Connect with world-renowned surgeons",
                  description:
                    "Network with ISAPS members and international aesthetic surgery leaders at exclusive congress events.",
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "16px",
                    padding: "32px 24px",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-4px)";
                    e.target.style.boxShadow = "0 8px 25px rgba(0,183,244,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      ...styles.flexV,
                      ...styles.gap20,
                    }}
                  >
                    <div
                      style={{
                        ...styles.flexH,
                        ...styles.gap20,
                      }}
                    >
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          backgroundColor: "rgba(0,183,244,0.1)",
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "20px",
                        }}
                      >
                        {feature.icon}
                      </div>
                      <div>
                        <h4
                          style={{
                            ...styles.fontSizeNormal,
                            ...styles.fontWeightBold,
                            marginBottom: "4px",
                          }}
                        >
                          {feature.title}
                        </h4>
                        <p
                          style={{
                            ...styles.fontSizeSmall,
                            ...styles.textColorGray,
                            margin: 0,
                          }}
                        >
                          {feature.subtitle}
                        </p>
                      </div>
                    </div>
                    <p
                      style={{
                        ...styles.fontSizeSmall,
                        color: colors.text,
                        lineHeight: "1.6",
                        margin: 0,
                      }}
                    >
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={styles.backgroundF3F3F3}>
        <div style={styles.outterWrapper}>
          <div style={styles.section}>
            <div
              style={{
                backgroundColor: colors.background,
                borderRadius: "16px",
                padding: "48px 32px",
                maxWidth: "900px",
                margin: "0 auto",
                ...styles.shadowBox,
              }}
            >
              <h3
                style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  textAlign: "center",
                  marginBottom: "32px",
                  color: colors.title,
                }}
              >
                What ISAPS Members Are Saying
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    borderLeft: `4px solid ${colors.primary}`,
                    paddingLeft: "16px",
                  }}
                >
                  <p
                    style={{
                      marginBottom: "8px",
                      fontSize: "16px",
                      color: colors.text,
                    }}
                  >
                    "ClinicOS has transformed how I present surgical options to
                    international patients. The AI accuracy is remarkable for
                    Asian facial aesthetics."
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: colors.title,
                      margin: 0,
                    }}
                  >
                    Dr. Li Wei Chen, Singapore General Hospital
                  </p>
                </div>
                <div
                  style={{
                    borderLeft: `4px solid ${colors.primary}`,
                    paddingLeft: "16px",
                  }}
                >
                  <p
                    style={{
                      marginBottom: "8px",
                      fontSize: "16px",
                      color: colors.text,
                    }}
                  >
                    "My consultation efficiency improved by 60%. Patients love
                    seeing culturally-appropriate before/after galleries."
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: colors.title,
                      margin: 0,
                    }}
                  >
                    Dr. Priya Sharma, Mumbai Aesthetic Institute
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - matches background-image-024767 */}
      <section id="register" style={styles.backgroundImage024767}>
        <div style={styles.outterWrapper}>
          <div
            style={{ ...styles.section, ...styles.centerDiv, ...styles.gap30 }}
          >
            <div
              style={{ ...styles.flexV, ...styles.gap30, textAlign: "center" }}
            >
              <h1 style={{ ...styles.h1, color: colors.white }}>
                Join Us in Singapore
              </h1>
              <p
                style={{
                  ...styles.fontSizeNormal,
                  color: "rgba(255, 255, 255, 0.9)",
                  maxWidth: "600px",
                  lineHeight: "1.5",
                }}
              >
                Reserve your spot at the ISAPS Olympiad World Congress 2025 and
                experience the future of aesthetic surgery with AI-powered
                patient consultations. Limited exclusive platform access
                available.
              </p>

              <button
                style={{
                  ...styles.priBtn,
                  marginBottom: "48px",
                }}
                onClick={() => handleCTAClick("final_cta", "signup")}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primaryDark;
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = colors.primary;
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Sign Up Now - Use Code ISAPS2025
              </button>

              <div
                style={{
                  ...styles.flexH,
                  justifyContent: "center",
                  ...styles.gap30,
                  flexWrap: "wrap",
                }}
              >
                <a
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Contact Us
                </a>
                <a
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Terms of Service
                </a>
                <a
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  Privacy Policy
                </a>
              </div>

              <p
                style={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                © Copyright ClinicOS 2025 • ISAPS Olympiad World Congress
                Partner
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Responsive CSS */}
      <style jsx>{`
        @media (max-width: 991px) {
          .nav-links {
            display: none !important;
          }
        }

        @media (max-width: 767px) {
          .section {
            padding: 60px 0 !important;
          }

          .h1 {
            font-size: 36px !important;
          }

          .h6 {
            font-size: 20px !important;
          }

          .flex-wrap-mobile {
            flex-wrap: wrap !important;
          }
        }

        @media (max-width: 479px) {
          .section {
            padding: 40px 0 !important;
          }

          .h1 {
            font-size: 28px !important;
          }

          .container {
            padding: 0 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ISAPSOlympiadLanding;
